import React, { useEffect, useState, useCallback } from "react";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { ClipboardList, CheckCircle2, Plus, Trash2, Loader2, Tag } from "lucide-react";

/**
 * ModalityPacksAdmin — Tier E
 * The practitioner picks their modality(ies): intake fields, taxonomy, protocol
 * templates and the AI guard-rail. The active pack constrains the Solunary assistant.
 */
export default function ModalityPacksAdmin() {
  const t = useT("admin");
  const { lang } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      // Both lists follow the dashboard language: a tenant pack is a reference to a
      // catalogue entry, and the server resolves its text in the `?lang=` asked for.
      const [tpl, mine] = await Promise.all([
        api.get(`/modality-packs/templates?lang=${lang}`),
        api.get(`/tenant/modality-packs?lang=${lang}`),
      ]);
      setTemplates(tpl.data);
      setPacks(mine.data);
    } catch (e) {
      toast.error(extractApiError(e, t("mod.error")));
    } finally {
      setLoading(false);
    }
  }, [t, lang]);

  useEffect(() => { load(); }, [load]);

  const assigned = new Set(packs.map((p) => p.slug));

  const assign = async (slug) => {
    setBusy(slug);
    try {
      await api.post("/tenant/modality-packs", { slug });
      toast.success(t("mod.added"));
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("mod.error")));
    } finally {
      setBusy(null);
    }
  };

  const activate = async (slug) => {
    setBusy(slug);
    try {
      await api.put(`/tenant/modality-packs/${slug}/activate`);
      toast.success(t("mod.activated"));
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("mod.error")));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(t("mod.confirmRemove"))) return;
    try {
      await api.delete(`/tenant/modality-packs/${slug}`);
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("mod.error")));
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-eh-muted" /></div>;

  return (
    <div data-testid="modality-packs-admin" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-sol-gold" /> {t("mod.title")}
        </h1>
        <p className="text-eh-muted text-sm mt-1">{t("mod.subtitle")}</p>
      </div>

      {packs.length > 0 && (
        <div className="space-y-3" data-testid="my-modalities">
          <h2 className="text-lg font-medium text-eh-ink">{t("mod.myModalities")}</h2>
          {packs.map((p) => (
            <div key={p.slug} className="bg-white rounded-2xl border border-eh-border-soft p-5" data-testid={`my-pack-${p.slug}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-xl">{p.name}</h3>
                  {p.active && (
                    <span className="text-xs bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`pack-active-${p.slug}`}>
                      <CheckCircle2 className="w-3 h-3" /> {t("mod.active")}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {!p.active && (
                    <Button size="sm" variant="outline" onClick={() => activate(p.slug)} disabled={busy === p.slug} data-testid={`activate-${p.slug}`}>
                      {t("mod.activate")}
                    </Button>
                  )}
                  <button onClick={() => remove(p.slug)} className="p-2 text-rose-500 hover:bg-rose-50 rounded" data-testid={`remove-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(p.taxonomy || []).map((tg) => (
                  <span key={tg} className="text-xs bg-eh-cream/60 text-eh-muted px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Tag className="w-2.5 h-2.5" />{tg}</span>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase text-eh-muted mb-1">{t("mod.intake", { count: (p.intake_fields || []).length })}</div>
                  <ul className="text-eh-ink space-y-0.5">
                    {(p.intake_fields || []).map((f) => <li key={f.key}>· {f.label}{f.required ? " *" : ""}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="text-xs uppercase text-eh-muted mb-1">{t("mod.protocols")}</div>
                  <ul className="text-eh-ink space-y-0.5">
                    {(p.protocols || []).map((pr) => <li key={pr.title}>· {pr.title} ({pr.steps.length} {t("mod.steps")})</li>)}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        <h2 className="text-lg font-medium text-eh-ink">{t("mod.catalog")}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {templates.filter((tp) => !assigned.has(tp.slug)).map((tp) => (
            <div key={tp.slug} className="bg-white rounded-2xl border border-eh-border-soft p-5" data-testid={`tpl-${tp.slug}`}>
              <h3 className="font-display text-lg mb-1">{tp.name}</h3>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(tp.taxonomy || []).slice(0, 5).map((tag) => (
                  <span key={tag} className="text-xs bg-eh-cream/60 text-eh-muted px-2 py-0.5 rounded-full">{tag}</span>
                ))}
              </div>
              <Button size="sm" onClick={() => assign(tp.slug)} disabled={busy === tp.slug} data-testid={`assign-${tp.slug}`} className="bg-eh-ink text-eh-bg">
                {busy === tp.slug ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                {t("mod.add")}
              </Button>
            </div>
          ))}
          {templates.filter((tp) => !assigned.has(tp.slug)).length === 0 && (
            <p className="text-eh-muted text-sm">{t("mod.allAdded")}</p>
          )}
        </div>
      </div>
    </div>
  );
}
